import { useState, useEffect, useCallback, useMemo } from 'react';
import { filter } from 'lodash';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  tableCellClasses,
  Typography,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { Box, styled } from '@mui/system';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// components
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserMoreMenu } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import Loader from '../Global/Loader';
// utils & services
import { fDate } from '../../utils/formatTime';
import RevenueService from '../services/RevenueService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getCustomerList } from '../../store/Slice/Customer/CustomerSlice';
import useDebounce from '../../utils/useDebounce';
import useCustomerSelector from '../../store/Slice/Customer/CustomerSelector';
import CustomerToolbar from './CustomerToolbar';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'customerId', label: 'CUSTOMER ID', sortable: true },
  { id: 'customerName', label: 'CUSTOMER NAME', sortable: true },
  { id: 'cityName', label: 'LOCATION', sortable: true },
  { id: 'relationshipSince', label: 'RELATIONSHIP SINCE', sortable: true },
  { id: 'mobile', label: 'MOBILE', sortable: true },
  { id: 'statusLabel', label: 'LOAN STATUS', sortable: true },
  { id: 'loanType', label: 'LOAN TYPE', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const list = Array.isArray(array) ? array : [];
  const stabilizedThis = list.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      list,
      (_user) => (_user.customerName || '').toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

const getStatusBadgeStyles = (status) => {
  const s = String(status || '').toLowerCase();
  if (
    s === 'active' ||
    s === 'completed' ||
    s === 'submitted' ||
    s === 'approved' ||
    s === 'matched'
  ) {
    return { bgcolor: '#dcfce7', color: '#16a34a' };
  }
  if (s === 'inactive' || s === 'negative' || s === 'rejected' || s === 'not matched') {
    return { bgcolor: '#fee2e2', color: '#dc2626' };
  }
  if (s === 'duplicate' || s === 'dnd' || s === 'pending' || s === 'warning') {
    return { bgcolor: '#fef3c7', color: '#d97706' };
  }
  if (s === 'new' || s === 'contacted') {
    return { bgcolor: '#e0f2fe', color: '#0284c7' };
  }
  return { bgcolor: '#f1f5f9', color: '#64748b' };
};

export default function CustomerList() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageMenu] = useState([]);
  const [openLoader] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [filterState, setFilterState] = useState({
    typeId: null,
    isActive: null,
    cityId: null
  });

  const { LoginData } = useLoginSelector();
  const dispatch = useDispatch();
  const debouncedSearch = useDebounce(filterName, 1000);
  const { CustomerListData } = useCustomerSelector();
  const { DownloadCustomerRecord } = RevenueService;

  const handleRequestSort = (property) => {
    if (property !== 'action') {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = (CustomerListData || []).map((n) => n.customerId);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const customerList = useMemo(
    () => (Array.isArray(CustomerListData) ? CustomerListData : []),
    [CustomerListData]
  );

  const filteredUsers = useMemo(
    () => applySortFilter(customerList, getComparator(order, orderBy), filterName),
    [customerList, order, orderBy, filterName]
  );

  const isUserNotFound = filteredUsers.length === 0;

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  useEffect(() => {
    setHasNextPage((CustomerListData?.length || 0) >= rowsPerPage);
  }, [CustomerListData, rowsPerPage]);

  const getCustomerListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      filterModel: {
        typeId: filterState.typeId || null,
        isActive: filterState.isActive || null,
        cityId: filterState.cityId || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 1 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      }
    };
    dispatch(getCustomerList(credentials));
  }, [LoginData, filterState, rowsPerPage, page, order, debouncedSearch, dispatch]);

  useEffect(() => {
    getCustomerListApi();
  }, [getCustomerListApi]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filterState]);

  const handleDownload = async () => {
    const credentials = {
      userId: LoginData?.userId,
      customerIds: selected,
      listModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      },
      filters: {
        typeId: null,
        cityId: null,
        isActive: true
      }
    };

    try {
      const response = await DownloadCustomerRecord(credentials, LoginData?.jwt);
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();

      const fileName = `CustomerDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.xlsx`;
      const mimeType =
        contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const totalCount = customerList.length;
  const startRecord = totalCount > 0 ? page * rowsPerPage + 1 : 0;
  const endRecord = totalCount > 0 ? page * rowsPerPage + totalCount : 0;

  return (
    <Page title="Partner Management System || Customer">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <PersonOutlineOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Customer List
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                View and manage customer profiles, loan statuses, and relationship history.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Main Content Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <CustomerToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            parent="Customer"
            pageMenu={pageMenu}
            filterState={filterState}
            setFilterState={setFilterState}
            setFilterName={setFilterName}
            handleDownload={handleDownload}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 900, minHeight: 380 }}>
              <Table>
                <thead>
                  <TableRow>
                    <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < customerList.length}
                        checked={customerList.length > 0 && selected.length === customerList.length}
                        onChange={handleSelectAllClick}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                        }}
                      />
                    </StyledHeadCell>
                    {TABLE_HEAD.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                        sx={{
                          cursor: headCell.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                          spacing={0.5}
                        >
                          <span>{headCell.label}</span>
                          {headCell.sortable && (
                            <SwapVertIcon
                              sx={{
                                fontSize: 16,
                                color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                              }}
                            />
                          )}
                        </Stack>
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {filteredUsers.map((row) => {
                    const {
                      cityName,
                      customerId,
                      customerName,
                      mobile,
                      relationshipSince,
                      statusLabel,
                      loanType
                    } = row;
                    const isItemSelected = selected.indexOf(customerId) !== -1;
                    const badgeStyles = getStatusBadgeStyles(statusLabel);

                    return (
                      <TableRow
                        hover
                        key={customerId}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                        sx={{
                          '&:hover': { backgroundColor: '#f8fafc !important' },
                          '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: 'rgba(2, 132, 199, 0.06)'
                          }
                        }}
                      >
                        <StyledTableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClick(customerId)}
                            sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#0284c7' } }}
                          />
                        </StyledTableCell>

                        <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                          {customerId}
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {customerName}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {cityName || 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {relationshipSince ? fDate(relationshipSince) : 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {mobile || 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 1.5,
                              py: 0.35,
                              borderRadius: '20px',
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              bgcolor: badgeStyles.bgcolor,
                              color: badgeStyles.color
                            }}
                          >
                            {statusLabel || 'N/A'}
                          </Box>
                        </StyledTableCell>

                        <StyledTableCell sx={{ color: '#475569' }}>
                          {loanType || 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell align="center" width={80}>
                          <UserMoreMenu rowId={customerId} parent="customer" pageMenu={pageMenu} />
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell
                        align="center"
                        colSpan={TABLE_HEAD.length + 1}
                        sx={{ py: 6 }}
                      >
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderTop: '1px solid #f1f5f9',
              gap: 2
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Showing{' '}
              <span style={{ fontWeight: 600, color: '#334155' }}>
                {startRecord} to {endRecord}
              </span>{' '}
              records
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    height: 32,
                    fontSize: 13,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                    px: 1
                  }}
                >
                  {page + 1}
                </Box>

                <IconButton
                  size="small"
                  disabled={totalCount < rowsPerPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>
        <Loader openLoader={openLoader} />
      </Container>
    </Page>
  );
}
